import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaCrop, FaTimes, FaCheck, FaSearchPlus, FaSearchMinus } from 'react-icons/fa';
import '../styles/ImageCropper.css';

const ImageCropper = ({ 
  file, 
  onCropComplete, 
  onCancel,
  onSkip,
  aspectRatio = 16/9,
  aspectRatioLabel = "16:9 (Landscape)",
  minWidth = 800,
  maxWidth = 1920,
  quality = 0.8
}) => {
  const [image, setImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [cropAreaSize, setCropAreaSize] = useState({ width: 0, height: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Load the image when file changes
  useEffect(() => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setImage({
          src: e.target.result,
          width: img.width,
          height: img.height,
          element: img
        });
        // Reset position and scale
        setPosition({ x: 0, y: 0 });
        setScale(1);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }, [file]);

  // Calculate crop area size based on container
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateCropArea = () => {
      const container = containerRef.current;
      const containerWidth = container.clientWidth - 40; // padding
      const containerHeight = container.clientHeight - 120; // space for controls
      
      let cropWidth, cropHeight;
      
      // Calculate crop area to fit within container while maintaining aspect ratio
      if (containerWidth / containerHeight > aspectRatio) {
        cropHeight = Math.min(containerHeight * 0.8, 400);
        cropWidth = cropHeight * aspectRatio;
      } else {
        cropWidth = Math.min(containerWidth * 0.9, 600);
        cropHeight = cropWidth / aspectRatio;
      }
      
      setCropAreaSize({ width: cropWidth, height: cropHeight });
    };
    
    updateCropArea();
    window.addEventListener('resize', updateCropArea);
    return () => window.removeEventListener('resize', updateCropArea);
  }, [aspectRatio]);

  // Calculate initial scale to fit image in crop area
  useEffect(() => {
    if (!image || cropAreaSize.width === 0) return;
    
    const scaleX = cropAreaSize.width / image.width;
    const scaleY = cropAreaSize.height / image.height;
    const initialScale = Math.max(scaleX, scaleY) * 1.1; // Slightly larger to allow movement
    
    setScale(initialScale);
    setPosition({ x: 0, y: 0 });
  }, [image, cropAreaSize]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !image) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Calculate bounds
    const scaledWidth = image.width * scale;
    const scaledHeight = image.height * scale;
    const maxX = Math.max(0, (scaledWidth - cropAreaSize.width) / 2);
    const maxY = Math.max(0, (scaledHeight - cropAreaSize.height) / 2);
    
    setPosition({
      x: Math.max(-maxX, Math.min(maxX, newX)),
      y: Math.max(-maxY, Math.min(maxY, newY))
    });
  }, [isDragging, dragStart, image, scale, cropAreaSize]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  // Touch events for mobile
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !image) return;
    const touch = e.touches[0];
    
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    
    const scaledWidth = image.width * scale;
    const scaledHeight = image.height * scale;
    const maxX = Math.max(0, (scaledWidth - cropAreaSize.width) / 2);
    const maxY = Math.max(0, (scaledHeight - cropAreaSize.height) / 2);
    
    setPosition({
      x: Math.max(-maxX, Math.min(maxX, newX)),
      y: Math.max(-maxY, Math.min(maxY, newY))
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleZoom = (delta) => {
    if (!image) return;
    
    const minScale = Math.max(
      cropAreaSize.width / image.width,
      cropAreaSize.height / image.height
    );
    const maxScale = minScale * 4;
    
    setScale(prev => Math.max(minScale, Math.min(maxScale, prev + delta)));
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    handleZoom(delta);
  };

  const cropImage = async () => {
    if (!image) return;
    
    setIsProcessing(true);
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Calculate the crop area in original image coordinates
      const scaledWidth = image.width * scale;
      const scaledHeight = image.height * scale;
      
      // Center of the scaled image
      const centerX = scaledWidth / 2;
      const centerY = scaledHeight / 2;
      
      // Crop area position relative to scaled image center
      const cropX = centerX - cropAreaSize.width / 2 - position.x;
      const cropY = centerY - cropAreaSize.height / 2 - position.y;
      
      // Convert to original image coordinates
      let sourceWidth = cropAreaSize.width / scale;
      let sourceHeight = cropAreaSize.height / scale;
      // Rounding can push the rectangle a hair outside the image, which would
      // draw transparent edges into the result; keep it inside the bitmap.
      let sourceX = Math.max(0, Math.min(cropX / scale, image.width - sourceWidth));
      let sourceY = Math.max(0, Math.min(cropY / scale, image.height - sourceHeight));
      sourceWidth = Math.min(sourceWidth, image.width - sourceX);
      sourceHeight = Math.min(sourceHeight, image.height - sourceY);

      // Output at the size actually cropped, capped at maxWidth. Upscaling to
      // minWidth was only inflating the file, so it is left as a warning below.
      const outputWidth = Math.min(maxWidth, Math.round(sourceWidth));
      const outputHeight = Math.round(outputWidth / aspectRatio);

      if (outputWidth < minWidth) {
        console.warn(`Cropped area is ${outputWidth}px wide, under the ${minWidth}px this slot expects.`);
      }
      
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      
      // Draw the cropped portion
      ctx.drawImage(
        image.element,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, outputWidth, outputHeight
      );
      
      // Convert to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Create a new file from the blob
          // The canvas always writes JPEG, so the name has to agree: the upload
          // endpoints pick their file type from the extension.
          const jpegName = (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg';
          const croppedFile = new File([blob], jpegName, { type: 'image/jpeg' });
          onCropComplete(croppedFile, canvas.toDataURL('image/jpeg', quality));
        }
        setIsProcessing(false);
      }, 'image/jpeg', quality);
    } catch (err) {
      console.error('Crop failed:', err);
      setIsProcessing(false);
    }
  };

  if (!file) return null;

  return (
    <div className="image-cropper-overlay">
      <div className="image-cropper-modal" ref={containerRef}>
        <div className="image-cropper-header">
          <h3><FaCrop style={{ marginRight: 8 }} /> Crop Image</h3>
          <button className="image-cropper-close" onClick={onCancel}>
            <FaTimes />
          </button>
        </div>
        
        <div className="image-cropper-info">
          <span>Recommended: <strong>{aspectRatioLabel}</strong></span>
          <span className="image-cropper-hint">Drag to reposition • Scroll to zoom</span>
        </div>
        
        <div 
          className="image-cropper-area"
          onWheel={handleWheel}
        >
          {image && (
            <div 
              className="image-cropper-viewport"
              style={{
                width: cropAreaSize.width,
                height: cropAreaSize.height
              }}
            >
              <div className="image-cropper-grid">
                <div className="grid-line horizontal" style={{ top: '33.33%' }} />
                <div className="grid-line horizontal" style={{ top: '66.66%' }} />
                <div className="grid-line vertical" style={{ left: '33.33%' }} />
                <div className="grid-line vertical" style={{ left: '66.66%' }} />
              </div>
              
              <img
                ref={imageRef}
                src={image.src}
                alt="Crop preview"
                className="image-cropper-image"
                style={{
                  width: image.width * scale,
                  height: image.height * scale,
                  // -50% here resolves against the image's own size, so it really is centred
                  transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
                  cursor: isDragging ? 'grabbing' : 'grab'
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                draggable={false}
              />
            </div>
          )}
          
          {!image && (
            <div className="image-cropper-loading">Loading image...</div>
          )}
        </div>
        
        <div className="image-cropper-controls">
          <div className="image-cropper-zoom">
            <button 
              className="zoom-btn" 
              onClick={() => handleZoom(-0.1)}
              title="Zoom out"
            >
              <FaSearchMinus />
            </button>
            <input
              type="range"
              min="0.5"
              max="3"
              step="0.1"
              value={scale}
              onChange={(e) => {
                if (!image) return;
                const minScale = Math.max(
                  cropAreaSize.width / image.width,
                  cropAreaSize.height / image.height
                );
                setScale(Math.max(minScale, parseFloat(e.target.value)));
              }}
              className="zoom-slider"
            />
            <button 
              className="zoom-btn" 
              onClick={() => handleZoom(0.1)}
              title="Zoom in"
            >
              <FaSearchPlus />
            </button>
          </div>
          
          <div className="image-cropper-actions">
            <button 
              className="image-cropper-btn cancel" 
              onClick={onCancel}
              disabled={isProcessing}
            >
              <FaTimes style={{ marginRight: 6 }} /> Cancel
            </button>
            {onSkip && (
              <button 
                className="image-cropper-btn skip" 
                onClick={() => onSkip(file)}
                disabled={isProcessing}
              >
                Skip Cropping
              </button>
            )}
            <button 
              className="image-cropper-btn confirm" 
              onClick={cropImage}
              disabled={isProcessing || !image}
            >
              {isProcessing ? 'Processing...' : (
                <><FaCheck style={{ marginRight: 6 }} /> Apply Crop</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
